import { useCallback, useMemo, useEffect } from 'react';
import { useFetcher } from '@remix-run/react';
import type { RemoveCollaboratorMutation } from 'generated/graphql';
import { REMOVE_BUILD_HUB_COLLABORATOR_API_ROUTE } from '~/routes/remix-api.app.build-hub-collaborator-remove';

interface UseBuildHubCollaboratorRemoveOptions {
    onSuccess?: () => void;
}

interface UseBuildHubCollaboratorRemoveReturn {
    removeCollaborator: (formData: FormData) => void;
    isUpdating: boolean;
    error: string | null;
    isSuccess: boolean;
    isIdle: boolean;
}

/**
 * Hook to handle collaborator remove submissions using Remix fetcher
 */
export function useBuildHubCollaboratorRemove(
    options: UseBuildHubCollaboratorRemoveOptions = {}
): UseBuildHubCollaboratorRemoveReturn {
    const { onSuccess } = options;
    const removeFetcher = useFetcher<RemoveCollaboratorMutation>();
    const data = removeFetcher.data?.buildHubCollaboratorRemove;
    const isUpdating = removeFetcher.state !== 'idle';
    const isIdle = !isUpdating;
    const isSuccess = Boolean(
        isIdle && data && data.__typename === 'BuildHubCollaborator'
    );

    const removeCollaborator = useCallback(
        (formData: FormData) => {
            removeFetcher.submit(formData, {
                method: 'post',
                action: REMOVE_BUILD_HUB_COLLABORATOR_API_ROUTE,
            });
        },
        [removeFetcher]
    );

    const error = useMemo(() => {
        if (isIdle && data && data.__typename !== 'BuildHubCollaborator') {
            return 'Message' in data
                ? data.Message
                : 'Failed to remove collaborator';
        }
        return null;
    }, [isIdle, data]);

    // Success handler
    useEffect(() => {
        if (isSuccess) {
            onSuccess?.();
        }
    }, [isSuccess, onSuccess]);

    return {
        removeCollaborator,
        isUpdating,
        error,
        isSuccess,
        isIdle,
    };
}
